"use client";
import { crypto_images } from "@/components/crypto_images";
import { ColumnDef } from "@tanstack/react-table";
import { ArrowDownLeftIcon, ArrowUpDown, ArrowUpRightIcon, CheckCircleIcon, HandCoinsIcon, Repeat2Icon, TimerIcon, XCircleIcon, ShieldXIcon, Loader2Icon } from "lucide-react";
import { Button } from "@/components/ui/button";
import React from "react";
import { Transaction } from "@/types/transaction-type";
import IdButton from "./IdButton";
import { Tooltip } from "@mui/material";
import Image from "next/image";
import { useTranslations } from "next-intl";
import { CurrencyIcon } from "@/components/common/CurrencyIcon";
import { formatTransactionFeeDisplay } from "@/utils/formatTransactionFee";

export const getColumns = (): ColumnDef<Transaction>[] => {
    const table_titles = useTranslations('table_titles');
    const transaction_statuses = useTranslations('transaction_statuses');
    return [
        {
            accessorKey: "id",
            header: ({ column }) => {
                return (
                    <Button
                        variant="ghost"
                        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                    >
                        {table_titles('id')}
                        <ArrowUpDown className="ml-2 h-4 w-4" />
                    </Button>
                );
            },
            cell: ({ row }) => {
                return <IdButton transaction={row.original} />;
            },
        },
        {
            accessorKey: "type",
            header: table_titles("type"),
            cell({ row }) {
                const type = row.original?.type?.toLowerCase();
                let element;

                if (type === 'deposit' || type === 'acquisition') element = <p className="flex items-center justify-center rounded-3xl w-[30px] h-[30px] bg-emerald-200 text-emerald-500 dark:bg-opacity-35 dark:text-emerald-300"><ArrowDownLeftIcon width={20} className="shrink-0" /></p>
                if (type === 'exchange') element = <p className="flex items-center justify-center rounded-3xl w-[30px] h-[30px] bg-brand-500/20 text-brand-600 dark:bg-brand-500/35 dark:text-brand-400"><Repeat2Icon width={20} className="shrink-0" /></p>
                if (type === 'withdrawal' || type === 'deduction') element = <p className="flex items-center justify-center rounded-3xl w-[30px] h-[30px] bg-red-300 text-red-600 dark:bg-opacity-35 dark:text-red-300"><ArrowUpRightIcon width={20} className="shrink-0" /></p>
                if (type === 'invoice') element = <p className="flex items-center justify-center rounded-3xl w-[30px] h-[30px] bg-orange-200 text-orange-500 dark:bg-opacity-35 dark:text-orange-300"><HandCoinsIcon width={15} className="shrink-0" /></p>

                return (<>{element && <Tooltip title={row.original.type} placement="top">{element}</Tooltip>}</>);
            },
        },
        {
            accessorKey: "date",
            header: table_titles("date"),
            cell: ({ row }) => (<p>{new Date(row.original.created_at).toLocaleDateString()} <span className="text-gray-500 text-xs">{new Date(row.original.created_at).toLocaleTimeString()}</span></p>)
        },
        {
            accessorKey: "base_asset",
            header: table_titles("base_asset"),
            cell: ({ row }) => {
                return (
                    <div className="flex items-center gap-2">
                        {row.original.base_asset ?
                            <>
                                <CurrencyIcon currencyCode={row.original.base_asset.toLowerCase()} width={32} height={32} alt="" className="rounded-full" />
{/* 
                                <Image
                                    src={crypto_images[row.original.base_asset.toLowerCase()]}
                                    width={30}
                                    height={30}
                                    alt={`Base Asset: ${row.original.base_asset}`}
                                /> */}
                                {row.original.base_asset}
                            </>
                            : row.original.base_asset
                        }
                    </div>
                );
            },
        },
        {
            accessorKey: "quote_asset",
            header: table_titles("quote_asset"),
            cell: ({ row }) => {
                return (
                    <div className="flex items-center gap-2">
                        {
                            row.original.quote_asset ?
                                <>
                                    <CurrencyIcon currencyCode={row.original.quote_asset.toLowerCase()} width={30} height={30} alt={`Quote Asset: ${row.original.quote_asset}`} />
                                    {row.original.quote_asset}
                                </>
                                : '-'
                        }
                    </div>
                );
            },
        },
        {
            accessorKey: "amount",
            header: table_titles("amount"),
            cell: ({ row }) => {
                const type = row.original.type?.toLowerCase();
                const formattedAmount = parseFloat(row.original.amount.toFixed(8)).toString();

                // Для Exchange: показываем sold amount с валютой (из quote_asset)
                if (type === 'exchange') {
                    const asset = row.original.quote_asset || '';
                    return asset ? `${formattedAmount} ${asset}` : formattedAmount;
                }

                // Для остальных: показываем amount с base_asset
                const asset = row.original.base_asset || '';
                return asset ? `${formattedAmount} ${asset}` : formattedAmount;
            },
        },
        {
            accessorKey: "usd_amount",
            header: table_titles("usd_amount"),
            cell: ({ row }) => {
                const type = row.original.type?.toLowerCase();

                // Для Exchange: показываем received amount с валютой (из base_asset)
                if (type === 'exchange') {
                    const formattedAmount = parseFloat(row.original.usd_amount.toFixed(8)).toString();
                    const asset = row.original.base_asset || '';
                    return asset ? `${formattedAmount} ${asset}` : formattedAmount;
                }

                // Для остальных: показываем в USD (это действительно USD)
                return `$ ${row.original.usd_amount.toLocaleString()}`;
            },
        },
        {
            accessorKey: "fee_pct",
            header: table_titles("fee"),
            cell: ({ row }) => formatTransactionFeeDisplay(row.original),
        },
        {
            accessorKey: "status",
            header: table_titles("status"),
            cell: ({ row }) => {
                const status = row.original.status.toLowerCase();
                if (status === 'pending') return (<p className="flex items-center justify-center gap-1 max-w-max font-semibold px-2 rounded-sm text-xs capitalize bg-slate-100 text-gray-500 dark:bg-slate-700 dark:text-gray-300"><TimerIcon width={15} className="shrink-0" />{transaction_statuses(row.original.status)}</p>);
                if (status === 'executed') return (<p className="flex items-center justify-center gap-1 max-w-max font-semibold px-2 rounded-sm text-xs capitalize bg-emerald-500 text-white"><CheckCircleIcon width={15} className="shrink-0" /> {transaction_statuses(row.original.status)}</p>);
                if (status === 'declined') return (<p className="flex items-center justify-center gap-1 max-w-max font-semibold px-2 rounded-sm text-xs capitalize bg-red-400 text-white"><XCircleIcon width={15} className="shrink-0" />{transaction_statuses(row.original.status)}</p>);
                if (status === 'expired') return (<p className="flex items-center justify-center gap-1 max-w-max font-semibold px-2 rounded-sm text-xs capitalize bg-red-600 text-white"><XCircleIcon width={15} className="shrink-0" />{transaction_statuses(row.original.status)}</p>);
                if (status === 'processing') return (<p className="flex items-center justify-center gap-1 max-w-max font-semibold px-2 rounded-sm text-xs capitalize bg-yellow-600 text-white"><Loader2Icon width={15} className="shrink-0 animate-spin" />{transaction_statuses(row.original.status)}</p>);
                if (status === 'blocked') return (<p className="flex items-center justify-center gap-1 max-w-max font-semibold px-2 rounded-sm text-xs capitalize bg-red-700 text-white"><ShieldXIcon width={15} className="shrink-0" />{transaction_statuses(row.original.status)}</p>);
            }
        },
    ];
}
